import React, { useState, useRef, useEffect } from 'react';
import { NAV_SECTIONS, SYSTEM_NAV, VIEW_TO_NAV, V4_NAV, V4_MORE } from '../../config/navigation';
import MobileBottomBar from './MobileBottomBar';
import MoreSheet from './dashboard/v4/MoreSheet';
import TasteModal from './dashboard/v4/TasteModal';
import EditProfileModal from './dashboard/v4/EditProfileModal';
import WorkspaceLayout from '../workspace/WorkspaceLayout';
import VerifyEmailBanner from '../auth/VerifyEmailBanner';
import VerifiedBadge from '../shared/VerifiedBadge';
import PageProgressBar from './PageProgressBar';
import { isHardcodedAdminEmail } from '../../lib/memory';
import CreditBalance from '../credits/CreditBalance';
import PlayerBar from '../player/PlayerBar';
import { isPlayerActive } from '../../lib/featureFlags';
import { storageImage } from '../../lib/img';

// Views that use the new three-zone workspace layout
const WORKSPACE_VIEWS = new Set([
  'shows', 'profile', 'builder', 'editor', 'episodes',
  'explore', 'analytics', 'labs', 'search',
  'invite', 'credits', 'account',
  'admin', 'help', 'support', 'workspaces', 'clip-upload', 'settings', 'billing',
  'marketplace', 'dashboard',
  'guest-profile', 'guest-profile-edit', 'inbox', 'matches', 'collabs',
  'partners', 'pricing', 'clips',
  // PR-A: closed-beta plans surface — wraps BillingSettingsPage with a
  // beta gate when paidPlansEnabled is off.
  'subscription',
  // Mini-spec #4: Shows Manager + Show Editor live inside the workspace
  // shell so the canonical 8-item sidebar (#2) renders alongside them.
  'shows-manager', 'show-editor',
  // Mini-spec #9: public hook leaderboard
  'leaderboard',
]);

// ─── SVG Icon set (14×14, matching mockup) ──────────────────────────────────
const ICONS = {
  dashboard:   <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="1" y="1" width="5" height="5" rx="1" fill="currentColor" opacity="0.9"/><rect x="8" y="1" width="5" height="5" rx="1" fill="currentColor" opacity="0.45"/><rect x="1" y="8" width="5" height="5" rx="1" fill="currentColor" opacity="0.45"/><rect x="8" y="8" width="5" height="5" rx="1" fill="currentColor" opacity="0.45"/></svg>,
  play:        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="5.5" stroke="currentColor" strokeWidth="1"/><path d="M5.5 4.5L9.5 7L5.5 9.5V4.5Z" fill="currentColor" opacity="0.8"/></svg>,
  list:        <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 3h10M2 7h7M2 11h9" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round"/></svg>,
  analytics:   <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 10L5 6.5L8 8.5L12 3" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round"/></svg>,
  signals:     <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="3" cy="7" r="1.5" fill="currentColor" opacity="0.7"/><circle cx="7" cy="4" r="1.5" fill="currentColor" opacity="0.9"/><circle cx="11" cy="7" r="1.5" fill="currentColor" opacity="0.7"/><path d="M4.4 6.5L5.7 4.8M8.3 4.8L9.6 6.5" stroke="currentColor" strokeWidth="0.9" opacity="0.5"/></svg>,
  hooks:       <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 12V5L7 2L12 5V12" stroke="currentColor" strokeWidth="1" strokeLinecap="round" strokeLinejoin="round"/><rect x="5" y="8" width="4" height="4" rx="0.5" fill="currentColor" opacity="0.5"/></svg>,
  marketplace: <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="1.5" y="3.5" width="11" height="7" rx="1" stroke="currentColor" strokeWidth="1"/><path d="M7 5.5V8.5M5.5 7H8.5" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/></svg>,
  deals:       <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M2 10L5 6.5L8 8L12 4" stroke="currentColor" strokeWidth="1.1" strokeLinecap="round" strokeLinejoin="round"/><circle cx="12" cy="4" r="1.5" fill="currentColor" opacity="0.7"/></svg>,
  network:     <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><path d="M7 1.5L8.5 5.5H12.5L9.5 7.5L10.5 11.5L7 9L3.5 11.5L4.5 7.5L1.5 5.5H5.5Z" stroke="currentColor" strokeWidth="0.9" strokeLinejoin="round"/></svg>,
  person:      <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="5" r="2.5" stroke="currentColor" strokeWidth="1"/><path d="M2 12c0-2.761 2.239-4 5-4s5 1.239 5 4" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/></svg>,
  settings:    <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><circle cx="7" cy="7" r="2" stroke="currentColor" strokeWidth="1"/><path d="M7 1.5V3M7 11V12.5M12.5 7H11M3 7H1.5M11 3L9.8 4.2M4.2 9.8L3 11M11 11L9.8 9.8M4.2 4.2L3 3" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/></svg>,
  billing:     <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="1.5" y="2.5" width="11" height="9" rx="1" stroke="currentColor" strokeWidth="1"/><path d="M4.5 2.5V1.5M9.5 2.5V1.5" stroke="currentColor" strokeWidth="1" strokeLinecap="round"/></svg>,
  admin:       <svg width="14" height="14" viewBox="0 0 14 14" fill="none"><rect x="1" y="1" width="5" height="5" rx="1" fill="currentColor" opacity="0.9"/><rect x="8" y="1" width="5" height="2" rx="0.5" fill="currentColor" opacity="0.5"/><rect x="8" y="5" width="5" height="2" rx="0.5" fill="currentColor" opacity="0.5"/><rect x="1" y="8" width="12" height="2" rx="0.5" fill="currentColor" opacity="0.5"/><rect x="1" y="12" width="8" height="1" rx="0.5" fill="currentColor" opacity="0.3"/></svg>,
  // Legacy icons for compatibility
  home:        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>,
  compose:     <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>,
  library:     <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M3 4h4v16H3zM10 4h4v16h-4zM17 4h4l-1 16h-3z"/></svg>,
  help:        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"/><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>,
  search:      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>,
  signout:     <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/></svg>,
  menu:        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>,
  chevron:     <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>,
  // v4
  plus:        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>,
  shortform:   <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M13 2L4.09 12.97a.5.5 0 0 0 .39.83H10v8.21c0 .67.86.94 1.24.39l8.67-12.97a.5.5 0 0 0-.41-.83H14V2.4c0-.67-.86-.94-1.24-.4z"/></svg>,
  compass:     <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="9"/><polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" fill="currentColor" opacity="0.35"/></svg>,
  studio:      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M3 18v-6a9 9 0 0 1 18 0v6"/><path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z"/></svg>,
  flask:       <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M9 3h6M10 3v7.5L5 20a1 1 0 0 0 .9 1.5h12.2A1 1 0 0 0 19 20l-5-9.5V3"/></svg>,
  handshake:   <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="m11 17 2 2a1 1 0 0 0 3-3"/><path d="m14 14 2.5 2.5a1 1 0 0 0 3-3l-3.88-3.88a3 3 0 0 0-4.24 0l-.88.88a1 1 0 0 1-1.41 0l-2.72-2.72a1 1 0 0 0-1.41 0L3 9.5a3 3 0 0 0 0 4.24L5.5 16.24"/><path d="m21 3-2.5 2.5"/><path d="M3 3l2.5 2.5"/></svg>,
  clips:       <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="5" y="2" width="14" height="20" rx="2"/><polygon points="10 9 15 12 10 15" fill="currentColor" stroke="none"/></svg>,
  more:        <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="5" cy="12" r="1.8"/><circle cx="12" cy="12" r="1.8"/><circle cx="19" cy="12" r="1.8"/></svg>,
  bell:        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M18 8a6 6 0 0 0-12 0c0 7-3 9-3 9h18s-3-2-3-9"/><path d="M13.73 21a2 2 0 0 1-3.46 0"/></svg>,
  inbox:       <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><polyline points="22 12 16 12 14 15 10 15 8 12 2 12"/><path d="M5.45 5.11 2 12v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2v-6l-3.45-6.89A2 2 0 0 0 16.76 4H7.24a2 2 0 0 0-1.79 1.11z"/></svg>,
  // PR-A: closed-beta crown for the Subscription dropdown row.
  crown:       <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6l4 5 5-7 5 7 4-5v13a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1z"/></svg>,
};

// ─── Niche pill ──────────────────────────────────────────────────────────────
function NichePill({ niche }) {
  if (!niche) return null;
  // Split niche into tags for display
  const tags = niche.toLowerCase().includes(',')
    ? niche.split(',').map(t => t.trim())
    : [niche];
  return (
    <div className="v2-niche-pill">
      <div className="v2-niche-label">Your niche</div>
      <div className="v2-niche-tags">
        {tags.map(t => <span key={t} className="v2-ntag">{t}</span>)}
      </div>
    </div>
  );
}

// ─── Avatar dropdown ──────────────────────────────────────────────────────────
function AvatarMenu({ user, profile, onNavigate, onSignOut }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const initials = (profile?.full_name?.[0] || user?.email?.[0] || '?').toUpperCase();

  useEffect(() => {
    if (!open) return;
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button className="topnav-avatar-btn" onClick={() => setOpen(v => !v)} aria-label="Account menu">
        {profile?.avatar_url
          ? <img src={storageImage(profile.avatar_url, { width: 96 })} alt="" className="topnav-avatar-img" />
          : <span className="topnav-avatar-initials">{initials}</span>}
      </button>
      {open && (
        <div className="topnav-avatar-dropdown">
          <div className="topnav-dd-header">
            <div className="topnav-dd-name">
              {profile?.full_name || user?.email?.split('@')[0]}
              <VerifiedBadge verified={profile?.verified} />
            </div>
            <div className="topnav-dd-email">{user?.email}</div>
            {profile?.username && (
              <div className="topnav-dd-handle" style={{ marginTop: 4, color: 'var(--gold, #ffb101)', fontSize: 12, fontWeight: 500 }}>
                @{profile.username}
              </div>
            )}
          </div>
          <div className="topnav-dd-divider" />
          {/* PR-A: avatar dropdown is the canonical entry to Profile,
              Subscription, and Account. Theme switcher intentionally
              omitted (deferred to a later build). */}
          <button className="topnav-dd-item" onClick={() => { setOpen(false); onNavigate('profile'); }}>
            {ICONS.person} Profile
          </button>
          <button className="topnav-dd-item" onClick={() => { setOpen(false); onNavigate('subscription'); }}>
            {ICONS.crown} Subscription
          </button>
          <button className="topnav-dd-item" onClick={() => { setOpen(false); onNavigate('account'); }}>
            {ICONS.settings} Account
          </button>
          <div className="topnav-dd-divider" />
          <button className="topnav-dd-item topnav-dd-item-danger" onClick={() => { setOpen(false); onSignOut(); }}>
            {ICONS.signout} Sign out
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Mobile top bar ───────────────────────────────────────────────────────────
function MobileTopBar({ isDeepView, canGoBack, showExit, onBack, onExitToHome, user, profile, onNavigate, onSignOut, onOpenAuth, onOpenMenu }) {
  return (
    <div className="mobile-topbar">
      <div className="mobile-topbar-left">
        {/* Hamburger on deep views (brief/thinking/results) where the bottom
            bar is hidden, so internal pages can still reach every page via
            the slide-out menu. Main pages use the bottom bar's "More". */}
        {user && isDeepView && (
          <button className="mobile-topbar-menu" onClick={onOpenMenu} aria-label="Open menu" aria-haspopup="menu">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/>
            </svg>
          </button>
        )}
        {isDeepView && canGoBack ? (
          <button className="mobile-topbar-back" onClick={onBack} aria-label="Go back">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="15 18 9 12 15 6"/>
            </svg>
          </button>
        ) : (
          <div className="mobile-topbar-brand">
            <span className="sb-brand-wordmark">epio<span className="sb-brand-dot">.</span></span>
          </div>
        )}
      </div>
      <div className="mobile-topbar-right">
        {isDeepView && showExit ? (
          <button className="mobile-topbar-exit" onClick={onExitToHome}>Exit</button>
        ) : user ? (
          <AvatarMenu user={user} profile={profile} onNavigate={onNavigate} onSignOut={onSignOut} />
        ) : (
          <button className="pub-nav-join" style={{ fontSize: 13, padding: '7px 14px' }} onClick={() => onOpenAuth?.('signup')}>
            Join free
          </button>
        )}
      </div>
    </div>
  );
}

// ─── V2 Sidebar nav item ─────────────────────────────────────────────────────
function V2NavItem({ item, active, collapsed, onClick, hasNotification }) {
  return (
    <button
      className={`v2-nav-item${active ? ' active' : ''}${item.accent ? ' v2-nav-record' : ''}`}
      onClick={() => onClick(item.view)}
      title={collapsed ? item.label : undefined}
    >
      <span className="v2-nav-icon">
        {ICONS[item.icon] || ICONS.home}
        {hasNotification && <span className="v2-notif-dot" />}
      </span>
      {!collapsed && (
        <>
          <span className="v2-nav-label">{item.label}</span>
          {item.badge && (
            <span className={`v2-nav-badge ${item.badgeType === 'new' ? 'v2-badge-new' : item.badgeType === 'hot' ? 'v2-badge-hot' : ''}`}>
              {item.badge}
            </span>
          )}
        </>
      )}
    </button>
  );
}

// ─── V4 Sidebar (Suno-style) ────────────────────────────────────────────────
function Sidebar({ view, collapsed, onNavigate, user, profile, onToggle, onSignOut, episodes, onOpenCommandPalette }) {
  const activeNav = VIEW_TO_NAV[view] || view;
  const initials = (profile?.full_name?.[0] || user?.email?.[0] || '?').toUpperCase();
  const [moreOpen, setMoreOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [notifOpen, setNotifOpen] = useState(false);
  const [notifCount, setNotifCount] = useState(0);
  const [profileMenu, setProfileMenu] = useState(false);
  const [tasteOpen, setTasteOpen] = useState(false);
  const profileRef = useRef(null);
  const isAdmin = user?.email?.endsWith('@episodeai.com')
    || isHardcodedAdminEmail(user?.email)
    || !!profile?.is_admin;
  const moreItems = isAdmin
    ? [...V4_MORE, { id: 'admin', label: 'Admin', view: 'admin' }]
    : V4_MORE;

  // Welcome notification on first sign-in
  useEffect(() => {
    if (!user) return;
    try {
      const key = `episode_notifs_${user.id || user.email}`;
      const raw = localStorage.getItem(key);
      const list = raw ? JSON.parse(raw) : null;
      if (!list) {
        const firstName = profile?.full_name?.split(' ')[0] || 'there';
        const welcome = [{
          id: 'welcome',
          title: `Welcome, ${firstName} 👋`,
          body: 'Epio is built for creators who want to move before the trend hits. Drop a topic, hit Build, and we do the rest.',
          ts: Date.now(),
          unread: true,
        }];
        localStorage.setItem(key, JSON.stringify(welcome));
        setNotifCount(1);
      } else {
        setNotifCount(list.filter(n => n.unread).length);
      }
    } catch {}
  }, [user, profile]);

  function getNotifs() {
    try {
      const key = `episode_notifs_${user.id || user.email}`;
      return JSON.parse(localStorage.getItem(key) || '[]');
    } catch { return []; }
  }
  function markAllRead() {
    try {
      const key = `episode_notifs_${user.id || user.email}`;
      const list = getNotifs().map(n => ({ ...n, unread: false }));
      localStorage.setItem(key, JSON.stringify(list));
      setNotifCount(0);
    } catch {}
  }

  // Profile menu close-on-outside-click
  useEffect(() => {
    if (!profileMenu) return;
    function handler(e) { if (profileRef.current && !profileRef.current.contains(e.target)) setProfileMenu(false); }
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [profileMenu]);

  return (
    <>
      <aside className={`app-sidebar v4-sidebar${collapsed ? ' app-sidebar-collapsed v4-sidebar-collapsed' : ''}`}>
        {/* Brand */}
        <div className="v4-sb-brand">
          {collapsed
            ? <span className="v4-sb-wordmark v4-sb-wordmark-short">ep<span className="v4-sb-dot">.</span>ai</span>
            : <span className="v4-sb-wordmark">episode<span className="v4-sb-dot">.</span>ai</span>}
        </div>

        {/* Collapse toggle — simple < / > */}
        <button className="v4-sb-toggle" onClick={onToggle} aria-label={collapsed ? 'Expand' : 'Collapse'}>
          {collapsed ? '›' : '‹'}
        </button>

        {/* Spacer — 150px between logo and profile */}
        <div className="v4-sb-spacer" />

        {/* Profile block with dropdown caret */}
        {user && (
          <div className="v4-sb-profile-wrap" ref={profileRef}>
            <button className="v4-sb-profile" onClick={() => setProfileMenu(v => !v)}>
              <span className="v4-sb-avatar">
                {profile?.avatar_url
                  ? <img src={storageImage(profile.avatar_url, { width: 96 })} alt="" />
                  : <span>{initials}</span>}
              </span>
              {!collapsed && (
                <>
                  <span className="v4-sb-profile-info">
                    <span className="v4-sb-name">
                      {profile?.full_name || user.email?.split('@')[0]}
                      <VerifiedBadge verified={profile?.verified} />
                    </span>
                    <span className="v4-sb-plan">{profile?.plan || 'Free'} plan</span>
                  </span>
                  <span className={`v4-sb-caret${profileMenu ? ' open' : ''}`} aria-hidden>▾</span>
                </>
              )}
            </button>
            {profileMenu && (
              <div className="v4-sb-profile-menu">
                <div className="v4-sb-pm-head">
                  <div className="v4-sb-pm-name">
                    {profile?.full_name || user.email?.split('@')[0]}
                    <VerifiedBadge verified={profile?.verified} />
                  </div>
                  <div className="v4-sb-pm-email">{user.email}</div>
                </div>
                <button className="v4-sb-pm-item" onClick={() => { setProfileMenu(false); onNavigate('profile'); }}>View profile</button>
                <button className="v4-sb-pm-item" onClick={() => { setProfileMenu(false); setEditOpen(true); }}>Edit profile</button>
                <button className="v4-sb-pm-item" onClick={() => { setProfileMenu(false); setTasteOpen(true); }}>My Taste</button>
                <button className="v4-sb-pm-item" onClick={() => { setProfileMenu(false); onNavigate('settings'); }}>Account settings</button>
                <button className="v4-sb-pm-item" onClick={() => { setProfileMenu(false); onNavigate('billing'); }}>Billing & plan</button>
                <div className="v4-sb-pm-divider" />
                <button className="v4-sb-pm-item danger" onClick={() => { setProfileMenu(false); onSignOut?.(); }}>Sign out</button>
              </div>
            )}
          </div>
        )}

        {/* Credit balance pill + Upgrade CTA. Every Upgrade button
            routes to the 'subscription' view (the canonical entry),
            which is now a thin alias for BillingSettingsPage. */}
        {!collapsed && user && (
          <div style={{ padding: '0 12px 12px', display: 'flex', flexDirection: 'column', gap: 10, alignItems: 'stretch' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
              <CreditBalance
                onBuyTopup={() => onNavigate('billing')}
                onUpgrade={() => onNavigate('subscription')}
              />
            </div>
            {(profile?.plan || 'free') === 'free' && (
              <button className="v4-sb-upgrade" onClick={() => onNavigate('subscription')}>
                Upgrade to Creator
              </button>
            )}
          </div>
        )}

        {/* Nav */}
        <nav className="v4-sb-nav">
          {V4_NAV.map(item => (
            <div
              key={item.id}
              style={{
                position: 'relative',
                display: 'flex', alignItems: 'stretch', gap: 0,
              }}
            >
              <button
                className={`v4-sb-item${activeNav === item.id ? ' active' : ''}${item.id === 'short-form' ? ' v4-sb-item--shortform' : ''}${item.id === 'create' ? ' v4-sb-item--create' : ''}`}
                onClick={() => onNavigate(item.view)}
                title={collapsed ? `${item.label}${item.badge ? ` · ${item.badge}` : ''}` : undefined}
                style={{ flex: 1 }}
              >
                <span className="v4-sb-icon">{ICONS[item.icon] || ICONS.home}</span>
                {!collapsed && <span className="v4-sb-label">{item.label}</span>}
                {!collapsed && item.badge && (
                  <span className={`v4-sb-nav-badge v4-sb-nav-badge--${item.badgeType || 'new'}`}>
                    {item.badge}
                  </span>
                )}
              </button>
              {/* Accessory "+ Create" button on the Clips nav item — pops
                  the UploadClipModal. Visible label so the affordance reads
                  as a CTA, not a mystery glyph. Dispatches a window event
                  that ClipsPage listens for after navigating. */}
              {!collapsed && item.accessoryAction === 'upload-clip' && (
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    // Navigate first so ClipsPage is mounted.
                    onNavigate(item.view);
                    // Then fire the event on the next tick.
                    setTimeout(() => {
                      window.dispatchEvent(new Event('clips:open-upload'));
                    }, 50);
                  }}
                  aria-label="Create new clip"
                  title="Create new clip"
                  style={{
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    gap: 3,
                    padding: '4px 8px', marginRight: 4,
                    border: '1px solid rgba(255, 177, 1, 0.35)',
                    borderRadius: 999,
                    background: 'rgba(255, 177, 1, 0.08)',
                    color: 'var(--gold, #ffb101)',
                    cursor: 'pointer',
                    fontSize: 11, fontWeight: 600,
                    lineHeight: 1,
                    fontFamily: 'inherit',
                  }}
                  type="button"
                >
                  <span style={{ fontSize: 13, lineHeight: 1, marginTop: -1 }}>+</span>
                  <span>Create</span>
                </button>
              )}
            </div>
          ))}
          {/* Notifications */}
          <button
            className="v4-sb-item"
            onClick={() => { setNotifOpen(true); markAllRead(); }}
            title={collapsed ? 'Notifications' : undefined}
          >
            <span className="v4-sb-icon">
              {ICONS.bell}
              {notifCount > 0 && <span className="v4-sb-notif-dot" />}
            </span>
            {!collapsed && <span className="v4-sb-label">Notifications</span>}
            {!collapsed && notifCount > 0 && <span className="v4-sb-count">{notifCount}</span>}
          </button>
        </nav>

        {/* The 60-sec script promo button used to live here as a separate
            pink-glow CTA. Promoted to a real V4_NAV item (icon + NEW
            badge alongside Home/Explore/Search/Create) so it's a
            permanent sidebar destination instead of a bottom-floating
            promo. The pink styling moves into a NEW-badge accent on
            the nav row itself (.v4-sb-item--shortform). */}

        {/* More — pinned at bottom */}
        <div className="v4-sb-bottom">
          <button
            className="v4-sb-item v4-sb-more"
            onClick={() => setMoreOpen(true)}
            title={collapsed ? 'More' : undefined}
          >
            <span className="v4-sb-icon">{ICONS.more}</span>
            {!collapsed && <span className="v4-sb-label">More</span>}
          </button>
        </div>
      </aside>

      <MoreSheet open={moreOpen} onClose={() => setMoreOpen(false)} items={moreItems} onPick={onNavigate} />
      <NotifSheet open={notifOpen} onClose={() => setNotifOpen(false)} notifs={getNotifs()} />
      <TasteModal open={tasteOpen} onClose={() => setTasteOpen(false)} user={user} profile={profile} />
      <EditProfileModal open={editOpen} onClose={() => setEditOpen(false)} user={user} profile={profile} />
    </>
  );
}

// ─── Notifications bottom sheet ─────────────────────────────────────────────
function NotifSheet({ open, onClose, notifs = [] }) {
  useEffect(() => {
    if (!open) return;
    function onKey(e) { if (e.key === 'Escape') onClose?.(); }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);
  return (
    <div className={`v4-sheet-wrap ${open ? 'open' : ''}`} aria-hidden={!open}>
      <div className="v4-sheet-backdrop" onClick={onClose} />
      <div className="v4-sheet" role="dialog" aria-label="Notifications">
        <div className="v4-sheet-handle" />
        <button className="v4-sheet-close" onClick={onClose} aria-label="Close">✕</button>
        <div className="v4-sheet-head">
          <div className="v4-sheet-title">Notifications</div>
          <div className="v4-sheet-sub">Messages from the Epio team</div>
        </div>
        {notifs.length === 0 ? (
          <div className="v4-notif-empty">You’re all caught up.</div>
        ) : (
          <div className="v4-notif-list">
            {notifs.map(n => (
              <div key={n.id} className="v4-notif-row v4-glass">
                <div className="v4-notif-title">{n.title}</div>
                <div className="v4-notif-body">{n.body}</div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Top nav (v4 — Suno-style, search top-right) ────────────────────────────
function TopNav({ user, profile, onOpenCommandPalette, onNavigate, onSignOut, onOpenAuth }) {
  return (
    <header className="app-topnav v4-topnav">
      <div className="v4-topnav-spacer" />
      <div className="v4-topnav-right">
        {user ? (
          <>
            <button className="v4-topnav-search" onClick={onOpenCommandPalette} aria-label="Search" title="Search (⌘K)">
              {ICONS.search}
            </button>
            <button className="v4-topnav-bell" aria-label="Notifications">
              {ICONS.bell}
              <span className="v4-topnav-bell-dot" />
            </button>
            <AvatarMenu user={user} profile={profile} onNavigate={onNavigate} onSignOut={onSignOut} />
          </>
        ) : (
          <>
            <button className="topnav-ghost-btn" onClick={() => onOpenAuth('signin')}>Sign in</button>
            <button className="topnav-cta-btn" onClick={() => onOpenAuth('signup')}>Join free</button>
          </>
        )}
      </div>
    </header>
  );
}

// ─── Mobile drawer overlay ────────────────────────────────────────────────────
function MobileNav({ open, onClose, view, onNavigate, user, profile, onSignOut }) {
  const activeNav = VIEW_TO_NAV[view] || view;
  useEffect(() => {
    document.body.style.overflow = open ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [open]);
  if (!open) return null;
  return (
    <>
      <div className="mobile-nav-backdrop" onClick={onClose} />
      <div className="mobile-nav-drawer">
        <div className="sb-brand" style={{ padding: '16px 16px 12px' }}>
          <div className="sb-brand-logo">
            <span className="sb-brand-wordmark">episode<span className="sb-brand-dot">.</span>ai</span>
          </div>
          <button onClick={onClose} style={{ marginLeft: 'auto', background: 'none', border: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 20 }}>✕</button>
        </div>
        <nav style={{ padding: '8px 12px', display: 'flex', flexDirection: 'column', gap: 2 }}>
          {NAV_SECTIONS.flatMap(s => s.items).map(item => (
            <button
              key={item.id}
              className={`sb-nav-item${activeNav === item.id ? ' sb-nav-item-active' : ''}`}
              onClick={() => { onNavigate(item.view); onClose(); }}
            >
              <span className="sb-nav-icon">{ICONS[item.icon] || ICONS.home}</span>
              <span className="sb-nav-label">{item.label}</span>
            </button>
          ))}
          {SYSTEM_NAV.filter(i => !NAV_SECTIONS.flatMap(s => s.items).find(n => n.id === i.id)).map(item => (
            <button
              key={item.id}
              className={`sb-nav-item${activeNav === item.id ? ' sb-nav-item-active' : ''}`}
              onClick={() => { onNavigate(item.view); onClose(); }}
            >
              <span className="sb-nav-icon">{ICONS[item.icon] || ICONS.home}</span>
              <span className="sb-nav-label">{item.label}</span>
            </button>
          ))}
        </nav>
        {user && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid var(--stroke)', marginTop: 'auto' }}>
            <div className="sb-user-footer" style={{ marginBottom: 10 }}>
              <div className="sb-user-avatar">
                <span>{(profile?.full_name?.[0] || user.email?.[0] || '?').toUpperCase()}</span>
              </div>
              <div className="sb-user-info">
                <div className="sb-user-name">
                  {profile?.full_name || user.email?.split('@')[0]}
                  <VerifiedBadge verified={profile?.verified} />
                </div>
                <div className="sb-user-plan">{user.email}</div>
              </div>
            </div>
            <button onClick={onSignOut} style={{ width: '100%', padding: '10px', borderRadius: 10, border: '1px solid var(--stroke)', background: 'none', color: 'var(--muted)', cursor: 'pointer', fontSize: 13 }}>
              Sign out
            </button>
          </div>
        )}
      </div>
    </>
  );
}

// ─── App shell — public (no sidebar) ─────────────────────────────────────────
function PublicShell({ children, onOpenAuth }) {
  return (
    <div className="public-shell v4-public-shell">
      {/* Logo top-left — text-only wordmark, matches the public
          landing nav. */}
      <div className="v4-pub-brand">
        <span className="v4-pub-wordmark">episode<span className="v4-pub-dot">.</span>ai</span>
      </div>
      {/* Sign in / Join free floating top-right */}
      <div className="v4-pub-actions">
        <button className="v4-pub-signin" onClick={() => onOpenAuth('signin')}>Sign in</button>
        <button className="v4-pub-join" onClick={() => onOpenAuth('signup')}>Join free</button>
      </div>
      <main style={{ flex: 1 }}>{children}</main>
    </div>
  );
}

// ─── Main export ──────────────────────────────────────────────────────────────
export default function AppShell({
  children, user, profile, loading, view, episodes,
  onNavigate, onOpenCommandPalette, onOpenAuth, onSignOut,
  isDeepView, canGoBack, showExit, onBack, onExitToHome, onStartBuilder, onAskEpio,
  rightPanel, rightPanelTitle,
  shows, activeShowId, onSelectShow, onManageShows,
  canCreateShow, onCreateShow,
  onOpenSearch, onOpenNotifications, notificationCount,
  inboxCount = 0,
}) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  if (loading) {
    // Branded splash on every viewport (was previously mobile-only, which
    // left desktop on a blank screen during the auth/boot gap). The epio.
    // wordmark + progress bar fills that gap instead.
    return (
      <div className="app-loading-v2">
        <div className="app-loading-bg" />
        <div className="app-loading-shade" />
        <div className="app-loading-content">
          <span className="app-loading-brand">
            epio<span className="app-loading-dot">.</span>
          </span>
          <div className="app-loading-bar" aria-hidden="true">
            <div className="app-loading-bar-fill" />
          </div>
        </div>
      </div>
    );
  }

  // The landing page is a full standalone pre-launch site — it carries
  // its own sticky nav and CTA, so it renders full-bleed with no app
  // chrome (no PublicShell wordmark / Sign in / Join free bar).
  if (view === 'landing') {
    return children;
  }

  // A signed-out guest still gets the full app workspace on the builder
  // view — /create is open to everyone (see EpisodeBuilderApp's
  // public-create gate) and the chat is meant to run inside the real app
  // shell, not a bare page. Every other view falls back to the minimal
  // PublicShell.
  const isCreateView = view === 'builder' || view === 'brief';
  if (!user && !isCreateView) {
    return <PublicShell onOpenAuth={onOpenAuth}>{children}</PublicShell>;
  }

  // Hide the persistent player bar on full-bleed feeds where its 64px
  // would steal scroll space the page already uses (mobile Clips uses a
  // fixed-position viewport-filling feed). Add other full-bleed views
  // here as they ship.
  const hidePlayer = view === 'clips';

  // Phase 1: new Suno-style workspace for 'shows' view. All other views keep
  // the existing 2-column AppShell until their redesign ships.
  if (WORKSPACE_VIEWS.has(view)) {
    return (
      <>
        <PageProgressBar view={view} />
        <VerifyEmailBanner />
        <WorkspaceLayout
          user={user}
          profile={profile}
          view={view}
          onNavigate={onNavigate}
          onSignOut={onSignOut}
          onOpenAuth={onOpenAuth}
          rightPanel={rightPanel}
          rightPanelTitle={rightPanelTitle}
          shows={shows}
          activeShowId={activeShowId}
          onSelectShow={onSelectShow}
          onManageShows={onManageShows}
          canCreateShow={canCreateShow}
          onCreateShow={onCreateShow}
          onOpenSearch={onOpenSearch}
          onOpenNotifications={onOpenNotifications}
          notificationCount={notificationCount}
          inboxCount={inboxCount}
          onAskEpio={onAskEpio}
        >
          {children}
        </WorkspaceLayout>

        {/* Mobile nav is now the hamburger drawer in WorkspaceLayout's top
            bar — the bottom tab bar was removed. */}

        {/* Persistent player bar — v1 placeholder, v1.5 active state via
            featureFlags.player. Sits at the bottom of every workspace view
            except full-bleed feeds (mobile Clips). Hidden entirely until
            v1.5 (Option B) — flip featureFlags.player to true when
            playback ships and the bar reappears with no other changes. */}
        {isPlayerActive() && (
          <div data-app-player-host style={playerBarHostStyles}>
            <PlayerBar hidden={hidePlayer} />
          </div>
        )}
      </>
    );
  }

  return (
    <div className={`app-shell${sidebarCollapsed ? ' app-shell-sidebar-collapsed' : ''}`}>
      <PageProgressBar view={view} />
      <VerifyEmailBanner />
      {/* Clips renders its own solid MobileFeedTopBar (wordmark + bell), so
          suppress the shared top bar there to avoid two stacked top bars. */}
      {view !== 'clips' && (
        <MobileTopBar
          isDeepView={!!isDeepView}
          canGoBack={!!canGoBack}
          showExit={!!showExit}
          onBack={onBack}
          onExitToHome={onExitToHome}
          user={user}
          profile={profile}
          onNavigate={onNavigate}
          onSignOut={onSignOut}
          onOpenAuth={onOpenAuth}
          onOpenMenu={() => setMobileNavOpen(true)}
        />
      )}
      <Sidebar
        view={view}
        collapsed={sidebarCollapsed}
        onNavigate={onNavigate}
        user={user}
        profile={profile}
        episodes={episodes}
        onToggle={() => setSidebarCollapsed(v => !v)}
        onSignOut={onSignOut}
        onOpenCommandPalette={onOpenCommandPalette}
      />
      <MobileNav
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        view={view}
        onNavigate={v => { onNavigate(v); setMobileNavOpen(false); }}
        user={user}
        profile={profile}
        onSignOut={onSignOut}
      />
      <main className="app-main-content">{children}</main>
      {user && !isDeepView && (
        <MobileBottomBar
          view={view}
          onNavigate={onNavigate}
          onCreateEpisode={onStartBuilder || (() => onNavigate('brief'))}
          onOpenMore={() => setMobileNavOpen(true)}
        />
      )}

      {/* Persistent player bar (legacy shell render path). Same positioning
          rules — fixed at the bottom, shifted up on mobile to clear the
          MobileBottomBar, hidden entirely on full-bleed feeds. Gated on
          isPlayerActive() so the v1 placeholder stays out of the way
          until clip + episode playback ships. */}
      {isPlayerActive() && (
        <div data-app-player-host style={playerBarHostStyles}>
          <PlayerBar hidden={hidePlayer} />
        </div>
      )}
    </div>
  );
}

// Player-bar host: fixed at the bottom, full width, sits ABOVE the
// MobileBottomBar via bottom-offset on narrow viewports. z-index just
// below MobileBottomBar (950) so the nav stays on top.
const playerBarHostStyles = {
  position: 'fixed',
  left: 0, right: 0, bottom: 0,
  zIndex: 940,
  pointerEvents: 'auto',
};

// MobileBottomBar height = ~64px; on narrow screens we lift the player
// up so it doesn't sit underneath the nav. Implemented via a CSS env
// trick: PlayerBarPlaceholder renders inside this host, which gets
// 64px bottom padding on mobile via the rule below.
if (typeof document !== 'undefined' && !document.getElementById('appshell-player-host-css')) {
  const s = document.createElement('style');
  s.id = 'appshell-player-host-css';
  s.textContent = `
    @media (max-width: 768px) {
      [data-app-player-host] { bottom: calc(64px + env(safe-area-inset-bottom, 0px)) !important; }
    }
  `;
  document.head.appendChild(s);
}
